import type { ApiResponse } from 'shared/api/responses';
import { api } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { UUID, SuccessResponse } from './types';

export type Footnote = ApiResponse<'/footnotes/document/{documentSuperId}', 'get'>[number];

export const footnoteQueryKeys = {
  footnotesByDocument: 'FOOTNOTES_BY_DOCUMENT',
  footnotesByTag: 'FOOTNOTES_BY_TAG',
} as const;

export function useFootnotesByDocument(documentSuperId: number | undefined): UseQueryResult<Footnote[]> {
  return useQuery({
    queryKey: [footnoteQueryKeys.footnotesByDocument, documentSuperId],

    queryFn: async () =>
      (await api.GET('/footnotes/document/{documentSuperId}', { params: { path: { documentSuperId: String(documentSuperId) } } }))?.data || [],

    enabled: !!documentSuperId,
  });
}

export function useFootnotesForTag(tagId: UUID | undefined): UseQueryResult<UUID[]> {
  return useQuery({
    queryKey: [footnoteQueryKeys.footnotesByTag, tagId],
    queryFn: async () => (await api.GET('/footnotes/tag/{tagId}', { params: { path: { tagId: tagId as string } } }))?.data || [],
    enabled: !!tagId,
  });
}

export function useCreateFootnote(): UseMutationResult<Footnote, unknown, { id: UUID; documentSuperId: number }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => (await api.POST('/footnotes/', { body: input }))?.data,
    onError: ServerErrorHandler,

    onSuccess: (_d, v) =>
      queryClient.invalidateQueries({
        queryKey: [footnoteQueryKeys.footnotesByDocument, v.documentSuperId],
      }),
  });
}

export function useDeleteFootnote(): UseMutationResult<SuccessResponse, unknown, { id: UUID; documentSuperId: number }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => (await api.DELETE('/footnotes/{id}', { params: { path: { id } } }))?.data,
    onError: ServerErrorHandler,

    onSuccess: (_d, v) => {
      queryClient.invalidateQueries({
        queryKey: [footnoteQueryKeys.footnotesByDocument, v.documentSuperId],
      });
      queryClient.invalidateQueries({
        queryKey: [footnoteQueryKeys.footnotesByTag],
      });
    },
  });
}

export function useAttachFootnote(): UseMutationResult<SuccessResponse, unknown, { footnoteId: UUID; tagId: UUID }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ footnoteId, tagId }) =>
      (await api.POST('/footnotes/{id}/tags/{tagId}', { params: { path: { id: footnoteId, tagId } }, body: {} }))?.data,

    onError: ServerErrorHandler,

    onSuccess: (_d, v) =>
      queryClient.invalidateQueries({
        queryKey: [footnoteQueryKeys.footnotesByTag, v.tagId],
      }),
  });
}

export function useDetachFootnote(): UseMutationResult<SuccessResponse, unknown, { footnoteId: UUID; tagId: UUID }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ footnoteId, tagId }) => (await api.DELETE('/footnotes/{id}/tags/{tagId}', { params: { path: { id: footnoteId, tagId } } }))?.data,

    onError: ServerErrorHandler,

    onSuccess: (_d, v) =>
      queryClient.invalidateQueries({
        queryKey: [footnoteQueryKeys.footnotesByTag, v.tagId],
      }),
  });
}
